import { computed, onMounted, onBeforeUnmount, shallowRef } from 'vue'
import { resolveStickyTop } from '@/utils/sticky-offset'

const APP_BAR_CLASS = 'v-app-bar'
const APP_BAR_SELECTOR = `.${APP_BAR_CLASS}`

// The safe-area spacer App.vue renders on notched devices. It is fixed and
// paints over the status strip, so a bar has to pin below it, not at 0.
const SYSTEM_BAR_SELECTOR = '.v-system-bar'

// Marks the branding band so StickyFilterBar can measure it and stack below.
// Owned here rather than by either component: it is the contract between them,
// and a rename that touched only one side would silently unstack the two.
export const STICKY_BRANDING_BAR_CLASS = 'StickyBrandingBar'

// The app bar slides over ~200ms, which outlasts the scroll event that started
// it, so keep sampling for a short tail rather than reading a single frame.
const SETTLE_FRAMES = 20

// App bars are async route components, so on first paint there may be nothing
// to measure yet. Watch longer at mount to catch one that arrives late.
const MOUNT_FRAMES = 120

/**
 * Drives the `top` of a `position: sticky` bar so it parks below the app bar
 * (and below the safe-area system bar on notched devices) in every scroll
 * state, rather than trusting Vuetify's `--v-layout-top`. See resolveStickyTop
 * for why that variable is not usable.
 *
 * Shared by StickyBrandingBar and StickyFilterBar: the branding band pins to
 * the app bar, and the filter bar pins to the band, so the two stack instead of
 * overlapping.
 *
 * Only for bars the PAGE scrolls. A bar inside a scrolling element must pin
 * flush to that box instead, which is a fact about the box, not something to
 * measure: that scroll container declares `--sticky-top: 0` and the bar reads
 * it straight from CSS. This used to be detected here by walking the bar's
 * ancestors, which failed open (an unresolved chain read as "the page scrolls
 * it") and cached the verdict, so a single bad read stranded the rich text
 * toolbar an app bar's height down its scroll box for the life of the screen.
 *
 * @param {string|null} pinnedAboveSelector selector for a sticky bar that pins
 *   directly above this one, or null when this bar clears the app bar itself
 * @returns {{ offsetStyle: import('vue').ComputedRef, offset: import('vue').ShallowRef }}
 *   inline style to bind (null while there is nothing to measure) and the raw
 *   offset in px, for callers that also have to reserve the space in JS
 */
export function useStickyTop(pinnedAboveSelector = null) {
  const offset = shallowRef(null)

  let frame = null
  let remainingFrames = 0

  const offsetStyle = computed(() => (offset.value === null ? null : { top: `${offset.value}px` }))

  const sample = () => {
    const appBar = document.querySelector(APP_BAR_SELECTOR)
    const systemBar = document.querySelector(SYSTEM_BAR_SELECTOR)
    const pinnedAbove = pinnedAboveSelector ? document.querySelector(pinnedAboveSelector) : null
    const next = resolveStickyTop(
      appBar?.getBoundingClientRect(),
      systemBar?.getBoundingClientRect(),
      pinnedAbove?.getBoundingClientRect(),
    )

    // A moving app bar means the slide is still in flight -- keep the tail alive.
    if (next !== offset.value) {
      offset.value = next
      remainingFrames = SETTLE_FRAMES
    }
  }

  const tick = () => {
    sample()
    frame = remainingFrames-- > 0 ? requestAnimationFrame(tick) : null
  }

  const schedule = (frames = SETTLE_FRAMES) => {
    remainingFrames = Math.max(remainingFrames, frames)
    if (frame === null) frame = requestAnimationFrame(tick)
  }

  // The app bar slides via a `transform` transition that can start well after the
  // scroll event that triggered it, so watch the transition itself rather than
  // relying on the scroll tail alone -- otherwise the offset goes stale mid-slide.
  const onAppBarTransition = (event) => {
    if (event.target?.classList?.contains(APP_BAR_CLASS)) schedule()
  }

  // Wrapped: passing `schedule` straight to addEventListener would feed the Event
  // object in as the frame budget.
  const resample = () => schedule()

  onMounted(() => {
    // Scheduled rather than sampled once: the app bar can mount after this bar on
    // routes that load it asynchronously.
    schedule(MOUNT_FRAMES)
    window.addEventListener('scroll', resample, { passive: true })
    window.addEventListener('resize', resample, { passive: true })
    document.addEventListener('transitionrun', onAppBarTransition, true)
    document.addEventListener('transitionend', onAppBarTransition, true)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', resample)
    window.removeEventListener('resize', resample)
    document.removeEventListener('transitionrun', onAppBarTransition, true)
    document.removeEventListener('transitionend', onAppBarTransition, true)
    if (frame !== null) cancelAnimationFrame(frame)
  })

  return { offsetStyle, offset }
}
